"use client";

import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useSession } from "next-auth/react";
import { useRouter, usePathname } from "next/navigation";
import { Badge, Button, IconButton, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import { Router as ServiceIcon } from "@mui/icons-material";
import { canViewServices } from "@/lib/admin/permissions";
import { SERVICE_ALERT_COUNT_QUERY_KEY } from "@/lib/admin/services/queryCache";

export default function AdminServiceButton() {
  const { data: session } = useSession();
  const router = useRouter();
  const pathname = usePathname();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isActive = pathname === "/admin/services";

  const hasServicesAccess = canViewServices(session);

  const { data: alertData } = useQuery({
    queryKey: SERVICE_ALERT_COUNT_QUERY_KEY,
    enabled: hasServicesAccess && !session?.impersonating,
    // Badge freshness only matters while the tab is visible, so poll slowly and
    // refresh on focus rather than running a timer in hidden tabs.
    refetchInterval: 900000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    // Shorter than the interval on purpose: refetchOnWindowFocus only fires
    // once the data is stale.
    staleTime: 120000,
    queryFn: async ({ signal }) => {
      const res = await fetch("/api/admin/services/alert-count", {
        method: "GET",
        cache: "no-store",
        signal,
      });
      if (!res.ok) {
        throw new Error("Failed to load service alert count");
      }
      return res.json();
    },
  });

  const alertCount = typeof alertData?.count === "number" ? alertData.count : 0;

  if (!hasServicesAccess || session?.impersonating) return null;

  const navigateToServiceAdmin = () => {
    const event = new CustomEvent("meetingNavigationRequest", {
      detail: { href: "/admin/services" },
      cancelable: true,
      bubbles: false,
    });
    if (document.dispatchEvent(event)) {
      router.push("/admin/services");
    }
  };

  return (
    <>
      {isMobile ? (
        <Tooltip
          title={
            alertCount > 0
              ? `Service Health (${alertCount} alert${
                  alertCount === 1 ? "" : "s"
                })`
              : "Service Health"
          }
        >
          <IconButton
            color="inherit"
            onClick={navigateToServiceAdmin}
            size="small"
            sx={{ ml: 1 }}
          >
            <Badge
              badgeContent={alertCount}
              color="error"
              invisible={alertCount === 0}
              max={99}
            >
              <ServiceIcon />
            </Badge>
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          variant="text"
          color="inherit"
          onClick={navigateToServiceAdmin}
          startIcon={
            <Badge
              badgeContent={alertCount}
              color="error"
              invisible={alertCount === 0}
              max={99}
            >
              <ServiceIcon />
            </Badge>
          }
          sx={{
            mr: 2,
            color: theme.palette.text.secondary,
            fontWeight: isActive ? 700 : 400,
            maxWidth: 200,
          }}
        >
          Service Admin
        </Button>
      )}
    </>
  );
}
